// externals
import type { Reducer } from 'redux';

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { DeskingState } from '@makemydeal/dr-dash-types';
import { actions as offerActions } from '@makemydeal/dr-offer-redux';
import { initActionTypes } from '@makemydeal/dr-shared-store';

// consts/enums
import {
    HIDE_TOTAL_TAX_RATE_SUPERSEDE_ALERT,
    PAYMENT_FAILURE,
    PAYMENT_RECEIVED,
    PAYMENT_REQUESTED,
    SET_FORCE_RELOAD_ON_WS_EVENT_TRIGGERED,
    SHOW_TOTAL_TAX_RATE_SUPERSEDE_ALERT,
    UPDATE_QUALIFICATION_IDS,
    AI_LENDER_PROBABILITY_SUCCESS,
    AI_VEHICLE_RECOMMENDATIONS_REQUEST,
    AI_VEHICLE_RECOMMENDATIONS_SUCCESS,
    AI_VEHICLE_RECOMMENDATIONS_FAILURE,
    AI_LENDER_PROBABILITY_REQUEST,
    AI_LENDER_PROBABILITY_FAILURE,
    SHOW_AI_REFRESH_BUTTON,
    HIDE_AI_REFRESH_BUTTON
} from '../actionTypes/deskingActionTypes';
import { PROGRAM_LOOKUP_EDIT_META } from '../constants';
import { getPSErrorPaymentDetails } from '../utils/psErrorUtils';
import type { QualificationId } from '@makemydeal/dr-shared-types';

export type DeskingReducer = Reducer<DeskingState>;

const initialState: DeskingState = {
    lastFailureAction: null,
    showTotalTaxRateSupersedeAlert: null,
    psErrorDetails: {
        lookupKey: null,
        actionForPayment: '',
        originalPSError: '',
        psErrorMessagePath: ''
    },
    qualificationIds: [],
    hasForceReloadOnWSEventTriggered: false,
    forceReloadSource: undefined,
    forceReloadMessage: undefined,
    aiVehicleRecommendations: [],
    aiVehicleRecommendationsLoading: false,
    lenderProbabilities: undefined,
    lenderProbabilitiesLoading: false,
    shouldShowAIRefreshButton: false
};

const failedActionType = (originalAction: AnyFSA): string =>
    originalAction.meta?.[PROGRAM_LOOKUP_EDIT_META]?.type ?? originalAction.type;

export const reducer: DeskingReducer = (state = initialState, action: AnyFSA) => {
    switch (action.type) {
        case initActionTypes.INIT_SUCCESS: {
            const qualificationIds: Array<QualificationId> = action.payload?.softPull;
            if (!qualificationIds) return state;
            return { ...state, qualificationIds };
        }
        case PAYMENT_REQUESTED:
            return { ...state, lastFailureAction: null };
        case PAYMENT_RECEIVED:
            if (action.meta == null) return state;
            if (action.meta.originalAction == null) return state;
            if (action.meta.offerType == null) return state;
            if (action.payload[action.meta.offerType]?.success !== true) {
                if (action.meta.originalAction.meta?.paymentGrid?.mainPaymentActionType) {
                    return { ...state, lastFailureAction: action.meta.originalAction.meta.paymentGrid.mainPaymentActionType };
                }
                return { ...state, lastFailureAction: failedActionType(action.meta.originalAction) };
            }

            return state;
        case PAYMENT_FAILURE:
            if (action.meta == null) return state;
            if (action.meta.originalAction == null) return state;
            if (action.meta.originalAction.meta?.paymentGrid?.mainPaymentActionType) {
                return {
                    ...state,
                    lastFailureAction: action.meta.originalAction.meta.paymentGrid.mainPaymentActionType,
                    psErrorDetails: {
                        ...getPSErrorPaymentDetails(action.meta, state.psErrorDetails)
                    }
                };
            }
            return {
                ...state,
                lastFailureAction: failedActionType(action.meta.originalAction),
                psErrorDetails: {
                    ...getPSErrorPaymentDetails(action.meta, state.psErrorDetails)
                }
            };

        case SHOW_TOTAL_TAX_RATE_SUPERSEDE_ALERT: {
            return { ...state, showTotalTaxRateSupersedeAlert: true };
        }

        case offerActions.UPDATED_OFFER_TYPE:
        case HIDE_TOTAL_TAX_RATE_SUPERSEDE_ALERT: {
            return { ...state, showTotalTaxRateSupersedeAlert: false };
        }
        case SET_FORCE_RELOAD_ON_WS_EVENT_TRIGGERED:
            return {
                ...state,
                hasForceReloadOnWSEventTriggered: action.payload.hasForceReloadOnWSEventTriggered,
                // Clear source/message on reset so a future true dispatch without them can't inherit stale values.
                forceReloadSource: action.payload.hasForceReloadOnWSEventTriggered ? action.payload.forceReloadSource : undefined,
                forceReloadMessage: action.payload.hasForceReloadOnWSEventTriggered ? action.payload.forceReloadMessage : undefined
            };

        // #region: AI Lender Probability Actions

        case AI_LENDER_PROBABILITY_REQUEST: {
            return {
                ...state,
                lenderProbabilitiesLoading: true,
                shouldShowAIRefreshButton: false
            };
        }

        case AI_LENDER_PROBABILITY_FAILURE:
        case AI_LENDER_PROBABILITY_SUCCESS: {
            const lenderProbabilities = action.payload?.data?.lenderProbabilities ?? action.payload?.lenderProbabilities;
            const lenderCreditSource = action.payload?.data?.dataSource ?? action.payload?.dataSource;
            const softPullCreditScore = action.payload?.data?.ficoScoreV8 ?? action.payload?.ficoScoreV8;
            const lenderAffordabilityLevel = action.payload?.data?.affordabilityLevel ?? action.payload?.affordabilityLevel;
            const lenderPredictedRate = action.payload?.data?.predictedRate ?? action.payload?.predictedRate;
            return {
                ...state,
                lenderProbabilities,
                lenderProbabilitiesLoading: false,
                ...(lenderCreditSource !== undefined && { lenderCreditSource }),
                ...(softPullCreditScore !== undefined && { softPullCreditScore }),
                ...(lenderAffordabilityLevel !== undefined && { lenderAffordabilityLevel }),
                ...(lenderPredictedRate !== undefined && { lenderPredictedRate })
            };
        }

        case UPDATE_QUALIFICATION_IDS: {
            return {
                ...state,
                qualificationIds: action.payload
            };
        }
        // #endregion: AI Lender Probability Actions

        // #region: AI Vehicle Recommendations Actions

        case AI_VEHICLE_RECOMMENDATIONS_REQUEST: {
            return {
                ...state,
                aiVehicleRecommendationsLoading: true,
                shouldShowAIRefreshButton: false
            };
        }

        case AI_VEHICLE_RECOMMENDATIONS_SUCCESS: {
            return {
                ...state,
                aiVehicleRecommendations: action.payload?.data?.recommendations || action.payload?.recommendations || [],
                aiVehicleRecommendationsLoading: false
            };
        }

        case AI_VEHICLE_RECOMMENDATIONS_FAILURE: {
            // Check if the request was aborted - if so, don't change state
            if (action.meta?.isAborted) {
                return state;
            }
            return {
                ...state,
                aiVehicleRecommendations: [],
                aiVehicleRecommendationsLoading: false
            };
        }

        // #endregion: AI Vehicle Recommendations Actions

        case SHOW_AI_REFRESH_BUTTON: {
            return {
                ...state,
                shouldShowAIRefreshButton: true
            };
        }

        case HIDE_AI_REFRESH_BUTTON: {
            return {
                ...state,
                shouldShowAIRefreshButton: false
            };
        }
    }
    return state;
};
